import { useState, useEffect } from 'react';
import { api } from '@/lib/api';
import { formatCOP } from '@/lib/utils';
import { TrendingUp, Home, DollarSign, Target, Clock, Percent, ArrowRight, CheckCircle2, Building2 } from 'lucide-react';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid } from 'recharts';

const COLORS = ['#A38560', '#8B6F47', '#C4A87C', '#725A3A', '#6BCB77', '#4EAAAA', '#E8C97D'];
const STAGE_COLORS = { lead_nuevo: '#3B82F6', contactado: '#8B5CF6', calificado: '#F59E0B', en_visitas: '#10B981', en_negociacion: '#EF4444', cerrado: '#6BCB77' };

function MetricCard({ label, value, icon: Icon, color = '#A38560' }) {
  return (
    <div className="bg-[#0A1F1A] rounded-2xl border border-white/5 p-4 lg:p-5 hover:border-[#A38560]/30 transition-all duration-300">
      <div className="flex items-center justify-between mb-2">
        <p className="text-xs uppercase tracking-wider text-white/40">{label}</p>
        <Icon size={18} strokeWidth={1.5} style={{ color }} />
      </div>
      <p className="font-outfit text-xl font-bold text-white tracking-tight tabular-nums">{value}</p>
    </div>
  );
}

const CustomPieLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }) => {
  if (percent < 0.05) return null;
  const RADIAN = Math.PI / 180;
  const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);
  return (
    <text x={x} y={y} fill="#fff" textAnchor="middle" dominantBaseline="central" fontSize={12} fontWeight="600">
      {`${(percent * 100).toFixed(0)}%`}
    </text>
  );
};

const PieTooltipContent = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const d = payload[0];
  return (
    <div className="bg-[#0A1F1A] border border-white/10 rounded-xl px-3 py-2 text-sm shadow-lg">
      <p className="text-white font-medium">{d.name}</p>
      <p className="text-white/60">{d.value}</p>
    </div>
  );
};

export default function Statistics({ userId }) {
  const [stats, setStats] = useState(null);
  const [crmFunnel, setCrmFunnel] = useState(null);

  useEffect(() => {
    const url = userId ? `/statistics?user_id=${userId}` : '/statistics';
    api.get(url).then(setStats).catch(() => {});
    const funnelUrl = userId ? `/statistics/crm-funnel?user_id=${userId}` : '/statistics/crm-funnel';
    api.get(funnelUrl).then(setCrmFunnel).catch(() => {});
  }, [userId]);

  if (!stats) return <div className="flex items-center justify-center h-64"><div className="animate-pulse font-outfit text-lg text-white/50">Cargando estadisticas...</div></div>;

  const clientSourceData = stats.source_table.filter(s => s.clients > 0).map(s => ({ name: s.source, value: s.clients }));
  const ownerSourceData = stats.source_table.filter(s => s.owners > 0).map(s => ({ name: s.source, value: s.owners }));

  return (
    <div data-testid="statistics-page" className="space-y-6">
      {!userId && <h1 className="font-outfit text-2xl lg:text-3xl font-medium tracking-tight text-white">Estadisticas</h1>}

      {/* Section: Resumen Financiero */}
      <div className="bg-[#0D2922] rounded-2xl border border-white/5 p-4 lg:p-6 space-y-4" data-testid="stats-financial-section">
        <h2 className="font-outfit text-base lg:text-lg font-medium text-[#A38560] tracking-wide uppercase">Resumen Financiero</h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3" data-testid="stats-metrics">
          <MetricCard label="Ventas Totales" value={formatCOP(stats.total_sales)} icon={TrendingUp} />
          <MetricCard label="Arriendos Totales" value={formatCOP(stats.total_rentals)} icon={Home} color="#6BCB77" />
          <MetricCard label="Comisiones Generadas" value={formatCOP(stats.total_commissions)} icon={DollarSign} />
          <MetricCard label="Comisiones Recibidas" value={formatCOP(stats.commissions_received)} icon={CheckCircle2} color="#6BCB77" />
          <MetricCard label="Negocios Cerrados" value={stats.deals_closed} icon={Target} color="#fff" />
        </div>
      </div>

      {/* Section: Rendimiento */}
      <div className="bg-[#0D2922] rounded-2xl border border-white/5 p-4 lg:p-6 space-y-4" data-testid="stats-performance-section">
        <h2 className="font-outfit text-base lg:text-lg font-medium text-[#A38560] tracking-wide uppercase">Rendimiento</h2>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <MetricCard label="Ticket Promedio Venta" value={formatCOP(stats.avg_ticket_sales)} icon={TrendingUp} />
          <MetricCard label="Ticket Promedio Arriendo" value={formatCOP(stats.avg_ticket_rentals)} icon={Home} color="#6BCB77" />
          <MetricCard label="Frecuencia de Cierre" value={`Cada ${stats.closing_frequency_days} dias`} icon={Clock} color="#fff" />
          <MetricCard label="Total Captaciones" value={stats.total_captures || 0} icon={Building2} color="#6BCB77" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <MetricCard label="% Neg. Venta" value={`${stats.avg_negotiation_pct_sales}%`} icon={Percent} color="#A38560" />
          <MetricCard label="% Neg. Arriendo" value={`${stats.avg_negotiation_pct_rentals}%`} icon={Percent} color="#6BCB77" />
        </div>
      </div>

      {/* Section: Fuentes - Pie Charts */}
      <div className="bg-[#0D2922] rounded-2xl border border-white/5 p-4 lg:p-6 space-y-4" data-testid="stats-sources-section">
        <h2 className="font-outfit text-base lg:text-lg font-medium text-[#A38560] tracking-wide uppercase">Fuentes</h2>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Pie: Fuentes de Clientes */}
          <div className="bg-[#0A1F1A] rounded-2xl border border-white/5 p-5" data-testid="client-sources-pie">
            <h3 className="font-outfit text-sm font-medium text-white mb-3">Fuentes de Clientes</h3>
            {clientSourceData.length > 0 ? (
              <div className="flex flex-col items-center">
                <ResponsiveContainer width="100%" height={220}>
                  <PieChart>
                    <Pie data={clientSourceData} cx="50%" cy="50%" innerRadius={50} outerRadius={90} paddingAngle={3} dataKey="value" labelLine={false} label={CustomPieLabel}>
                      {clientSourceData.map((entry, i) => <Cell key={entry.name} fill={COLORS[i % COLORS.length]} />)}
                    </Pie>
                    <Tooltip content={<PieTooltipContent />} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 justify-center">
                  {clientSourceData.map((s, i) => (
                    <div key={s.name} className="flex items-center gap-1.5 text-xs text-white/70">
                      <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: COLORS[i % COLORS.length] }} />
                      {s.name} ({s.value})
                    </div>
                  ))}
                </div>
              </div>
            ) : <p className="text-sm text-white/40 py-8 text-center">Sin datos de fuentes de clientes</p>}
          </div>

          {/* Pie: Fuentes de Propietarios */}
          <div className="bg-[#0A1F1A] rounded-2xl border border-white/5 p-5" data-testid="owner-sources-pie">
            <h3 className="font-outfit text-sm font-medium text-white mb-3">Fuentes de Propietarios</h3>
            {ownerSourceData.length > 0 ? (
              <div className="flex flex-col items-center">
                <ResponsiveContainer width="100%" height={220}>
                  <PieChart>
                    <Pie data={ownerSourceData} cx="50%" cy="50%" innerRadius={50} outerRadius={90} paddingAngle={3} dataKey="value" labelLine={false} label={CustomPieLabel}>
                      {ownerSourceData.map((entry, i) => <Cell key={entry.name} fill={COLORS[i % COLORS.length]} />)}
                    </Pie>
                    <Tooltip content={<PieTooltipContent />} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 justify-center">
                  {ownerSourceData.map((s, i) => (
                    <div key={s.name} className="flex items-center gap-1.5 text-xs text-white/70">
                      <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: COLORS[i % COLORS.length] }} />
                      {s.name} ({s.value})
                    </div>
                  ))}
                </div>
              </div>
            ) : <p className="text-sm text-white/40 py-8 text-center">Sin datos de fuentes de propietarios</p>}
          </div>
        </div>

        {/* Source Table */}
        {stats.source_table.length > 0 && (
          <div className="bg-[#0A1F1A] rounded-2xl border border-white/5 p-5" data-testid="source-table">
            <h3 className="font-outfit text-sm font-medium text-white mb-3">Detalle por Fuente</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-white/10">
                    <th className="text-left py-2.5 px-2 text-white/40 text-xs uppercase tracking-wider font-medium">Fuente</th>
                    <th className="text-center py-2.5 px-2 text-white/40 text-xs uppercase tracking-wider font-medium">Clientes</th>
                    <th className="text-center py-2.5 px-2 text-white/40 text-xs uppercase tracking-wider font-medium">Propietarios</th>
                    <th className="text-right py-2.5 px-2 text-white/40 text-xs uppercase tracking-wider font-medium">Valor Total</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.source_table.map((s) => (
                    <tr key={s.source} className="border-b border-white/5 hover:bg-white/5 transition-colors">
                      <td className="py-2.5 px-2 font-medium text-white">{s.source}</td>
                      <td className="text-center py-2.5 px-2 text-white">{s.clients}</td>
                      <td className="text-center py-2.5 px-2 text-white">{s.owners}</td>
                      <td className="text-right py-2.5 px-2 text-white tabular-nums">{formatCOP(s.value)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Section: Captaciones */}
      {stats.captures_by_month && stats.captures_by_month.length > 0 && (
        <div className="bg-[#0D2922] rounded-2xl border border-white/5 p-4 lg:p-6 space-y-4" data-testid="stats-captures-section">
          <h2 className="font-outfit text-base lg:text-lg font-medium text-[#A38560] tracking-wide uppercase">Captaciones</h2>
          <div className="bg-[#0A1F1A] rounded-2xl border border-white/5 p-5" data-testid="captures-chart">
            <h3 className="font-outfit text-sm font-medium text-white mb-3 flex items-center gap-2">
              <Building2 size={16} strokeWidth={1.5} className="text-[#A38560]" />
              Captaciones por Mes
            </h3>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={stats.captures_by_month} margin={{ left: 0, right: 20 }}>
                <XAxis dataKey="month" stroke="#666" fontSize={11} />
                <YAxis allowDecimals={false} stroke="#666" fontSize={11} />
                <Tooltip contentStyle={{ background: '#0A1F1A', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '12px', color: '#fff' }} formatter={(value) => [value, 'Captaciones']} />
                <Bar dataKey="count" radius={[8, 8, 0, 0]} barSize={32}>
                  {stats.captures_by_month.map((entry, i) => <Cell key={entry.month} fill={COLORS[i % COLORS.length]} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Section: Embudo de Conversion CRM */}
      <div className="bg-[#0D2922] rounded-2xl border border-white/5 p-4 lg:p-6 space-y-4" data-testid="stats-funnel-section">
        <h2 className="font-outfit text-base lg:text-lg font-medium text-[#A38560] tracking-wide uppercase">Embudo de Conversion CRM</h2>
        {crmFunnel ? (
          <>
            <div className="bg-[#0A1F1A] rounded-2xl border border-white/5 p-4">
              <div className="h-[240px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={crmFunnel.funnel} margin={{ left: 10, right: 10 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#1A3B32" />
                    <XAxis dataKey="label" stroke="#ffffff40" tick={{ fontSize: 10 }} />
                    <YAxis stroke="#ffffff40" tick={{ fontSize: 11 }} />
                    <Tooltip contentStyle={{ backgroundColor: '#0A1F1A', border: '1px solid #1A3B32', borderRadius: 12, color: '#fff' }}
                      formatter={(v, n, p) => [`${v} leads`, `${p.payload.pct}% de etapa anterior`]} />
                    <Bar dataKey="count" radius={[6, 6, 0, 0]}>
                      {crmFunnel.funnel.map((entry, i) => <Cell key={i} fill={STAGE_COLORS[entry.stage] || '#A38560'} />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {crmFunnel.funnel.map((step, i) => (
                <div key={step.stage} className="flex items-center gap-2">
                  <div className="bg-[#16302B] rounded-xl border border-white/5 px-3 py-2 text-center min-w-[80px]">
                    <p className="text-[10px] text-white/40">{step.label}</p>
                    <p className="text-lg font-outfit font-semibold text-white">{step.count}</p>
                  </div>
                  {i < crmFunnel.funnel.length - 1 && (
                    <div className="flex flex-col items-center">
                      <ArrowRight size={12} className="text-white/15" />
                      <span className="text-[9px] text-[#A38560] font-medium">{crmFunnel.funnel[i + 1].pct}%</span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </>
        ) : (
          <div className="text-center py-8 text-white/30 text-sm">Cargando embudo...</div>
        )}
      </div>
    </div>
  );
}
